import {defineStore, getActivePinia} from "pinia";
import { ref } from "vue";
import { authService } from "../services/authService";
import { instanceOfValidationResponse, ValidationResponse } from "../utils/helpers";
import { useLocalStorage } from "@vueuse/core";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { handleError } from "../utils/errors";
import * as Sentry from "@sentry/browser";

export interface LoginPayload {
    email: string;
    password: string;
    remember: boolean;
}

export interface RegisterPayload {
    nip: string;
    regon: string;
    branch_regon: string;
    firnam: string;
    lasnam: string;
    email: string;
    password: string;
    password_confirmation: string;
}

export interface ForgotPasswordPayload {
    email: string;
}

export interface ResetPasswordPayload {
    email: string;
    password: string;
    password_confirmation: string;
}

export interface SetNewPasswordPayload {
    email: string;
    password: string;
    password_confirmation: string;
}

export interface AuthStore {
    user: App.Resource.User | null;
    isGuest: boolean;
    isLoading: boolean;

    validationErrors: {
        login: ValidationResponse;
        register: ValidationResponse;
        resetPassword: ValidationResponse;
        setNewPassword: ValidationResponse;
    };
    hasError(field: string): boolean;
    getError(field: string): string;

    loginForm: LoginPayload;
    login();

    registerForm: RegisterPayload;
    register();

    forgotPasswordForm: ForgotPasswordPayload;
    sendForgotPasswordNotification();
    forgotPasswordContext: {
        message: string;
    };

    resetPasswordForm: ResetPasswordPayload;
    resetPassword();

    setNewPasswordForm: SetNewPasswordPayload;
    setNewPassword();

    logout();
    authUser();
}

export const useAuthStore = defineStore("auth", () => {
    const router = useRouter();
    const toast = useToast();

    const user = ref<App.Resource.User>(null);
    const isGuest = ref(useLocalStorage("auth.isGuest", true));

    const isLoading = ref(false);

    const loginForm = ref<LoginPayload>({
        email: "",
        password: "",
        remember: false,
    });

    const registerForm = ref<RegisterPayload>({
        nip: "",
        regon: "",
        branch_regon: "",
        firnam: "",
        lasnam: "",
        email: "",
        password: "",
        password_confirmation: "",
    });

    const forgotPasswordForm = ref<ForgotPasswordPayload>({
        email: "",
    });

    const resetPasswordForm = ref<ResetPasswordPayload>({
        email: "",
        password: "",
        password_confirmation: "",
    });

    const setNewPasswordForm = ref<SetNewPasswordPayload>({
        email: "",
        password: "",
        password_confirmation: "",
    });

    const validationErrors = ref({
        login: {
            errors: {},
            message: "",
        },
        register: {
            errors: {},
            message: "",
        },
        forgotPassword: {
            errors: {},
            message: "",
        },
        resetPassword: {
            errors: {},
            message: "",
        },
        setNewPassword: {
            errors: {},
            message: "",
        },
    });

    const hasError = (field: string): boolean => {
        const type = field.split(".")[0];
        const name = field.split(".")[1];
        return name in validationErrors.value[type].errors;
    };
    const getError = (field: string): string => {
        const type = field.split(".")[0];
        const name = field.split(".")[1];
        return hasError(field) ? validationErrors.value[type].errors[name][0] ?? "" : "";
    };

    const resetValidationErrors = (name: "login" | "register" | "forgotPassword" | "resetPassword" | "setNewPassword") =>
        (validationErrors.value[name] = { errors: {}, message: "" });
    const pushValidationErrors = (
        name: "login" | "register" | "forgotPassword" | "resetPassword" | "setNewPassword",
        data: ValidationResponse
    ) => (validationErrors.value[name] = data);

    async function login() {
        try {
            isLoading.value = true;
            resetValidationErrors("login");

            await authService.login(loginForm.value);
            await authUser();
            isGuest.value = false;
            await router.push({
                name: "portal.homepage",
            });
        } catch (error) {
            /**
             * Błąd zwrócony przez Laravela
             */
            if (instanceOfValidationResponse(error.data)) {
                pushValidationErrors("login", error.data);
            } else {
                toast.error(error.data.message ?? error.message);
            }
        } finally {
            isLoading.value = false;
        }
    }
    async function register() {
        try {
            isLoading.value = true;
            resetValidationErrors("register");

            await authService.register(registerForm.value);
            await authUser();
            isGuest.value = false;
            await router.push({
                name: "portal.homepage",
            });
        } catch (error) {
            /**
             * Błąd zwrócony przez Laravela
             */
            if (instanceOfValidationResponse(error.data)) {
                pushValidationErrors("register", error.data);
            } else {
                toast.error(error.data.message ?? error.message);
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function resetPassword() {
        try {
            isLoading.value = true;
            resetValidationErrors("resetPassword");
            const response = await authService.resetPassword({
                token: router.currentRoute.value.query.token ?? "",
                ...resetPasswordForm.value,
            });
            toast.success(response.data.message);
            await router.push({
                name: "auth.login",
            });
        } catch (error) {
            /**
             * Błąd zwrócony przez Laravela
             */
            if (instanceOfValidationResponse(error.data)) {
                pushValidationErrors("resetPassword", error.data);
            } else {
                toast.error(error.data.message ?? error.message);
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function setNewPassword() {
        try {
            isLoading.value = true;
            resetValidationErrors("setNewPassword");
            const response = await authService.setNewPassword({
                token: router.currentRoute.value.query.token ?? "",
                ...setNewPasswordForm.value,
            });
            toast.success(response.data.message);
            await router.push({
                name: "auth.login",
            });
        } catch (error) {
            /**
             * Błąd zwrócony przez Laravela
             */
            if (instanceOfValidationResponse(error.data)) {
                pushValidationErrors("setNewPassword", error.data);
            } else {
                toast.error(error.data.message ?? error.message);
            }
        } finally {
            isLoading.value = false;
        }
    }

    const forgotPasswordContext = ref({
        message: "",
    });
    async function sendForgotPasswordNotification() {
        try {
            isLoading.value = true;
            resetValidationErrors("forgotPassword");
            forgotPasswordContext.value.message = "";

            const response = await authService.forgotPassword(forgotPasswordForm.value);
            forgotPasswordContext.value.message = response.data.message;
        } catch (error) {
            /**
             * Błąd zwrócony przez Laravela
             */
            if (instanceOfValidationResponse(error.data)) {
                pushValidationErrors("forgotPassword", error.data);
            } else {
                toast.error(error.data.message ?? error.message);
            }
        } finally {
            isLoading.value = false;
        }
    }
    async function logout() {
        try {
            isLoading.value = true;
            await authService.logout();

            // Wyczyszczenie danych użytkownika
            isGuest.value = true;
            user.value = null;
            // Sentry: clear the currently set user.
            Sentry.setUser(null);

            await router.push({
                name: "auth.login",
            });

            getActivePinia()._s.forEach((store, name) => {
                const extNames = [
                    'auth',
                    'dialogs',
                    'forms',
                ];
                if(!extNames.includes(name)) {
                    store.$reset();
                    // store.$dispose();
                }
            });

        } catch (error) {
            handleError(error);
        } finally {
            isLoading.value = false;
        }
    }
    async function authUser() {
        try {
            isLoading.value = true;

            const response = await authService.user();
            isGuest.value = false;
            user.value = response.data.data;

            // Sentry: identify the user.
            Sentry.setUser({
                id: user.value.id.toString(),
            });
        } catch (error) {
            if (error && error?.status === 401) {
                isGuest.value = true;
                user.value = null;
            } else {
                handleError(error);
            }
        } finally {
            isLoading.value = false;
        }
    }

    return {
        forgotPasswordForm,
        isGuest,
        isLoading,
        loginForm,
        registerForm,
        user,
        validationErrors,
        authUser,
        sendForgotPasswordNotification,
        forgotPasswordContext,
        getError,
        hasError,
        login,
        logout,
        register,
        resetPasswordForm,
        resetPassword,
        setNewPasswordForm,
        setNewPassword,
    };
});
